import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DomainBadge from '../../../shared/components/DomainBadge';

/**
 * Full Matrix Recall: completely blank matrix, user fills every cell from memory.
 */
export default function FullRecall({ matrix }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const totalCells = useMemo(() => {
    if (!matrix) return 0;
    return matrix.attributes.length * matrix.protocols.length;
  }, [matrix]);

  if (!matrix) return null;

  const handleChange = (attrIdx, protocol, value) => {
    setAnswers(prev => ({ ...prev, [`${attrIdx}-${protocol}`]: value }));
  };

  const handleSubmit = () => setSubmitted(true);
  const handleRetry = () => { setAnswers({}); setSubmitted(false); };

  // Score
  let correctCount = 0;
  let attemptedCount = 0;

  if (submitted) {
    for (let attrIdx = 0; attrIdx < matrix.attributes.length; attrIdx++) {
      for (const protocol of matrix.protocols) {
        const key = `${attrIdx}-${protocol}`;
        const userVal = (answers[key] || '').trim().toLowerCase();
        const correctVal = (matrix.attributes[attrIdx].values[protocol] || '').toLowerCase();

        if (userVal.length > 0) attemptedCount++;
        if (userVal && (userVal === correctVal || correctVal.includes(userVal))) {
          correctCount++;
        }
      }
    }
  }

  const filledCount = Object.values(answers).filter(v => v?.trim()).length;

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate(`/matrix/${matrix.id}`)} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            ← Back to Matrix
          </button>
          <h2 className="text-xl font-bold text-gray-900">Full Recall: {matrix.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <DomainBadge domainId={matrix.domain} size="xs" />
            <span className="text-sm text-gray-500">
              Fill all {totalCells} cells from memory
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!submitted ? (
            <>
              <span className="text-sm text-gray-500">{filledCount}/{totalCells} filled</span>
              <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                Check Answers
              </button>
            </>
          ) : (
            <>
              <span className="text-lg font-bold" style={{
                color: (correctCount / totalCells) >= 0.7 ? '#2ECC71' : (correctCount / totalCells) >= 0.4 ? '#F39C12' : '#E74C3C'
              }}>
                {correctCount}/{totalCells} correct ({Math.round((correctCount / totalCells) * 100)}%)
              </span>
              <button onClick={handleRetry} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-medium text-gray-600 border-b border-r border-gray-200 min-w-[180px]">
                Attribute
              </th>
              {matrix.protocols.map(protocol => (
                <th key={protocol} className="px-4 py-3 text-left font-medium text-gray-900 border-b border-gray-200 min-w-[150px]">
                  {protocol}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.attributes.map((attr, attrIdx) => (
              <tr key={attrIdx} className={`border-b border-gray-100 ${
                attr.exam_hot ? 'bg-yellow-50' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
              }`}>
                <td className={`sticky left-0 z-10 px-4 py-3 font-medium border-r border-gray-200 ${
                  attr.exam_hot ? 'bg-yellow-50' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}>
                  {attr.exam_hot && <span className="text-yellow-500 text-xs mr-1">★</span>}
                  {attr.name}
                </td>
                {matrix.protocols.map(protocol => {
                  const key = `${attrIdx}-${protocol}`;
                  const correctVal = attr.values[protocol] || '';

                  if (!submitted) {
                    return (
                      <td key={protocol} className="px-2 py-2">
                        <input
                          type="text"
                          value={answers[key] || ''}
                          onChange={(e) => handleChange(attrIdx, protocol, e.target.value)}
                          placeholder="?"
                          className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        />
                      </td>
                    );
                  }

                  const userVal = (answers[key] || '').trim().toLowerCase();
                  const isCorrect = userVal && (userVal === correctVal.toLowerCase() || correctVal.toLowerCase().includes(userVal));
                  const isEmpty = !userVal;

                  return (
                    <td key={protocol} className={`px-4 py-3 ${
                      isEmpty ? 'bg-gray-100' : isCorrect ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      {!isEmpty && (
                        <div className={`text-sm font-medium ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                          {answers[key]}
                        </div>
                      )}
                      {(!isCorrect || isEmpty) && (
                        <div className="text-xs text-gray-500 mt-1">
                          {correctVal}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
